import Breadcrumb from '$store/components/ui/Breadcrumb.tsx'
import Button from '$store/components/ui/Button.tsx'
import Icon from '$store/components/ui/Icon.tsx'
import Slider from '$store/components/ui/Slider.tsx'
import SliderJS from '$store/islands/SliderJS.tsx'
import Text from '$store/components/ui/Text.tsx'
import ZoomableImage from '$store/islands/ZoomableImage.tsx'
import { SealConfig } from '$store/loaders/seals.tsx'
import { TimerConfig } from '$store/loaders/timers.tsx'
import { useId } from '$store/sdk/useId.ts'
import { useOffer } from '$store/sdk/useOffer.ts'
import { SimilarProduct, useSimilars as fetchSimilars } from '$store/sdk/useSimilars.ts'
import { useTimers } from '$store/sdk/useTimers.ts'
import { HTMLWidget, ImageWidget } from 'apps/admin/widgets.ts'
import type { ProductDetailsPage } from 'apps/commerce/types.ts'
import Image from 'apps/website/components/Image.tsx'
import { AppContext } from 'apps/website/mod.ts'
import { OutOfStockTexts } from 'site/components/product/OutOfStock.tsx'
import { SkusText } from 'site/components/product/ProductDetailsVariantSelector.tsx'
import { ShippingTexts } from 'site/components/ui/ShippingSimulation.tsx'
import { PaymentMethodsProps } from 'site/loaders/PaymentMethods.ts'
import calcOFF from 'site/sdk/calcOFF.ts'
// import type { Product as ProductSTD } from 'deco-sites/std/commerce/types.ts'
// import { ProductDetailsTemplate } from 'deco-sites/std/components/VTEXPortalDataLayerCompatibility.tsx'
import { type SectionProps } from '@deco/deco'
import ProductInfo from 'site/components/product/ProductInfo.tsx'
import { GlobalConfig } from 'site/apps/site.ts'
import { type Matcher as _Matcher } from '@deco/deco/blocks'
import { ProductAppPriceAdminProps } from 'site/components/product/ProductAppPrice.tsx'
import type { Product } from 'apps/commerce/types.ts'
export interface Texts {
    /**
     * @title Texto de parcelamento
     * @description Parcele em até {{duration}}x de {{price}}
     * @default Parcele em até {{duration}}x de {{price}}
     */
    installmentText: HTMLWidget
    /**
     * @title Texto do preço sem desconto
     * @description De: {{price}}
     * @default De: {{price}}
     */
    priceWithoutDiscountText: HTMLWidget
    /**
     * @title Texto do preço com desconto
     * @description Por: {{price}}
     * @default Por: {{price}}
     */
    priceWithDiscountText: HTMLWidget
    /**
     * @title Texto do preço caso o produto não tenha desconto
     * @description À partir de {{price}}
     * @default A partir de {{price}}
     */
    priceIfDontHaveDiscountText: HTMLWidget
    /**
     * @title Texto do código do produto
     * @description Cód: {{code}}
     * @default Cód: {{code}}
     */
    productCodeText: HTMLWidget
    /**
     * @title Texto do Pix
     * @description ou 3% de desconto no Pix
     * @default ou 3% de desconto no Pix
     */
    pixDiscountText: HTMLWidget

    /**
     * @title Mini Banner PDP
     * @urlText Url do link da imagem
     * @format datetime
     * @imageSrc Imagem do banner
     * @rule Regra de Aplicação
     */
    miniBannerPDP?: {
        /**
         * @title Start at date
         * @format datetime
         */
        startAt?: string
        /**
         * @title Expires at date
         * @format datetime
         */
        expiresAt?: string
        title: string
        urlText: string
        imageSrc: ImageWidget
    }
    /**
     * @title Texto dos SKUs
     * @description Selecione o tipo de texto para os SKUs
     */
    skusText: SkusText[]
    /**
     * @title Texto para o usuário passar o mouse em cima da imagem do produto
     */
    imageHoverText: string
    /**
     * @title Texto de tamanhos similares
     */
    similarSizesText: string
    /**
     * @title Texto de cores similares
     */
    similarColorsText: string
    shippingTexts: ShippingTexts
    outOfStockTexts: OutOfStockTexts
}
export interface Props {
    page: ProductDetailsPage | null
    /**
     * @title Selo
     */
    seals?: SealConfig[]
    /**
     * @title Cronômetro
     */
    timers?: TimerConfig[]
    /**
     * @title Textos
     * @description Customizar textos da página de produto
     */
    texts: Texts
    /**
     * @title Métodos de pagamentos
     */
    paymentMethods: PaymentMethodsProps
    /**
     * @title Texto do botão para abrir a modal de meios de pagamentos
     */
    paymentMethodsButtonText: string
    /**
     * @title Renderizar as imagens de 1 a 4?
     */
    imagesOneToFour: boolean
    /**
     * @title Renderizar as imagens de 5 a 8?
     */
    imagesFiveToEightRender: boolean

    /**
     * @title Não exibir retirada em loja, somente transportadoras.
     */
    renderOnlyShipping?: boolean

    /**
     * @title Variação de layout de similar
     */
    similarsVariantions?: boolean

    shippingVariant?: 'open' | 'closed'

    /**
     * @title App Preço Config
     */
    appPrice: ProductAppPriceAdminProps
    /**
     * @title Layout da Descrição do Produto
     * @description Escolha entre o layout padrão (accordion) ou alternativo (texto truncado com degradê).
     * @default default
     */
    descriptionVariant?: 'default' | 'alt'

    /**
     * @title Campaign Studio - WebStorie 
     */
    campaignStudio?: 'ativo' | 'desativo'
}
export type SimilarsProps = {
    similars: SimilarProduct[]
    texts?: Props['texts']
    layout?: 'buyModal'
}
const WIDTH = 480
const HEIGHT = 600
const ASPECT_RATIO = `${WIDTH} / ${HEIGHT}`
/**
 * Rendered when a not found is returned by any of the loaders run on this page
 */
function NotFound() {
    return (
        <div class='w-full flex justify-center items-center py-28'>
            <div class='flex flex-col items-center justify-center gap-6'>
                <Text variant='heading-2'>Página não encontrada</Text>
                <a href='/'>
                    <Button>Voltar à página inicial</Button>
                </a>
            </div>
        </div>
    )
}

function Details(
    {
        page,
        similars,
        selectedSkuId,
        seals,
        timers,
        href,
        texts,
        paymentMethods,
        paymentMethodsButtonText,
        pixDiscount,
        hidePixDiscountInProduct,
        imagesOneToFour,
        imagesFiveToEightRender,
        renderOnlyShipping,
        similarsVariantions,
        shippingVariant,
        appPrice,
        descriptionVariant,
        campaignStudio,
    }: {
        page: ProductDetailsPage
        href: string
        similars: SimilarProduct[]
        selectedSkuId: string | null
        seals: SealConfig[]
        timers: TimerConfig[]
        texts: Props['texts']
        paymentMethods: Props['paymentMethods']
        paymentMethodsButtonText: Props['paymentMethodsButtonText']
        pixDiscount?: number
        hidePixDiscountInProduct?: boolean
        imagesOneToFour: boolean
        imagesFiveToEightRender: boolean
        renderOnlyShipping?: boolean
        similarsVariantions?: boolean
        shippingVariant?: 'open' | 'closed'
        appPrice: ProductAppPriceAdminProps
        descriptionVariant?: 'default' | 'alt'
        campaignStudio?: 'ativo' | 'desativo'
    },
) {
    const id = `product-image-gallery-${useId()}`

    let {
        product: {
            image: images = [],
            offers,
            isAccessoryOrSparePartFor,
            additionalProperty = [], // name,
            // inProductGroupWithID,
        },
    } = page

    const formatImages = images && images?.length > 4 && imagesFiveToEightRender
        ? images?.slice(4, 8)
        : images && images?.length > 4 && imagesOneToFour
        ? images?.slice(0, 4)
        : images?.slice(0, images?.length) || null
    const { price, listPrice } = useOffer(offers)
    const hasDiscount = !!price && price > 0 && !!listPrice && listPrice > price
    const isKitLook = additionalProperty.find((property) => property.propertyID === '2268')
    const { breadcrumbList } = page
    images = isKitLook && isAccessoryOrSparePartFor
        ? isAccessoryOrSparePartFor.map(({ image = [] }) => image ? image[0] : null).filter(
            (item) => !!item,
        )
        : formatImages
    // const [image] = images
    // const { url } = image
    return (
        <>
            {/* Breadcrumb */}
            <div class='container max-w-[1248px] px-4 pb-8'>
                <div class='mb-2 mt-2 lg:mb-0 lg:mt-0'>
                    <Breadcrumb itemListElement={breadcrumbList?.itemListElement} />
                </div>
                {/* <ProductDetailsTemplate type='module' product={page.product as ProductSTD} /> */}

                <div
                    id={id}
                    class='flex flex-col lg:flex-row items-start justify-between gap-x-12 gap-y-6'
                >
                      <div class='flex gap-x-12 flex-col lg:flex-row lg:justify-between lg:max-w-[716px] lg:w-full w-full sticky top-[100px]'>
                        <div class='relative mt-6 lg:mt-0 flex lg:flex-col max-lg:justify-center items-center gap-y-2 order-1 lg:-order-1 lg:max-w-[480px] max-h-[600px]'>
                            <Slider.PrevButton
                                id={id}
                                class='max-md:p-3 max-lg:absolute top-1/2 md:left-[0px] left-[-18px] max-lg:-translate-y-1/2 grid place-content-center disabled:opacity-50 transition-opacity'
                            >
                                <Icon
                                    height={24}
                                    width={15}
                                    id='ChevronLeft'
                                    class='text-neutral-2 lg:rotate-90 transition-opacity disabled:opacity-50'
                                />
                            </Slider.PrevButton>

                            {/* Dots */}
                            <Slider.DotGroup class='flex lg:flex-col gap-2 max-w-xs overflow-auto max-h-[550px] scrollbar h-full justify-between'>
                                {images?.map((img, i) => {
                                    return (
                                        <Slider.Dot index={i}>
                                            <div class='gap-1.5 !justify-start overflow-auto sm:px-0 sm:flex-col sm:col-start-1 sm:col-span-1 row-start-1 sm:row-start-1'>
                                                <Image
                                                    style={{ aspectRatio: ASPECT_RATIO }}
                                                    class='opacity-50 group-data-[active="1"]:border-2 group-data-[active="1"]:border-primary-700 group-data-[active="1"]:opacity-100 transition-all min-w-[63px] sm:min-w-[100px] object-cover'
                                                    width={100}
                                                    height={124}
                                                    src={img.url ?? ''}
                                                    alt={img.alternateName}
                                                />
                                            </div>
                                        </Slider.Dot>
                                    )
                                })}
                            </Slider.DotGroup>

                            <Slider.NextButton
                                id={id}
                                class='max-md:p-3 max-lg:absolute top-1/2 md:right-[0px] right-[-18px] max-lg:-translate-y-1/2 grid place-content-center disabled:opacity-50 transition-opacity'
                            >
                                <Icon
                                    height={24}
                                    width={15}
                                    id='ChevronRight'
                                    class='text-neutral-2 lg:rotate-90'
                                />
                            </Slider.NextButton>
                        </div>

                        {/* Image Slider */}
                        <div class='relative flex justify-center lg:flex-col md:col-start-2 md:col-span-1 h-fit row-start-1 sm:row-start-1'>
                            {hasDiscount && (
                                <div class='abacatte px-2 py-0.5 bg-secondary-700 absolute top-3 right-3 text-white font-bold z-10'>
                                    {calcOFF(listPrice, price).toFixed(0)}% OFF
                                </div>
                            )}

                            <Slider class='gap-6 carousel w-[480px] max-w-[95vw]'>
                                {images?.map((img, index) => (
                                    <Slider.Item
                                        index={index}
                                        class='carousel-item sm:w-[480px] w-screen'
                                    >
                                        <ZoomableImage
                                            factor={2}
                                            type='hover'
                                            class='hidden lg:block'
                                            sizes='(max-width: 640px) 100vw, 38vw'
                                            style={{ aspectRatio: ASPECT_RATIO }}
                                            src={img.url ?? ''}
                                            alt={img.alternateName}
                                            width={WIDTH}
                                            height={HEIGHT}
                                            preload={index === 0}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'low'}
                                        />
                                        <ZoomableImage
                                            factor={2}
                                            type='click'
                                            class='max-w-[100vw] block lg:hidden md:min-w-[50vw] 2xl:min-w-[38vw]'
                                            sizes='(max-width: 640px) 100vw, 38vw'
                                            style={{ aspectRatio: ASPECT_RATIO }}
                                            src={img.url ?? ''}
                                            alt={img.alternateName}
                                            width={WIDTH}
                                            height={HEIGHT}
                                            preload={index === 0}
                                            loading={index === 0 ? 'eager' : 'lazy'}
                                            fetchPriority={index === 0 ? 'high' : 'low'}
                                        />
                                    </Slider.Item>
                                ))}

                                {/* Dots */}
                                <Slider.DotGroup class='flex gap-x-1 absolute bottom-4 lg:bottom-9 left-1/2 -translate-x-1/2'>
                                    {images?.map((_, i) => {
                                        return (
                                            <Slider.Dot index={i}>
                                                <div class='w-4 h-1.5 rounded group-data-[active]:w-9 bg-white group-data-[active]:bg-primary-700 transition-all' />
                                            </Slider.Dot>
                                        )
                                    })}
                                </Slider.DotGroup>
                            </Slider>

                            {/* Discount */}
                            {
                                /* {discountPercentage > 0 && (
        <Badge value={discountPercentage} class='top-2 right-2' />
    )} */
                            }

                            {/* Arrows */}
                            <Slider.PrevButton
                                id={id}
                                class='absolute left-0 lg:-left-12 top-[45%] h-10 w-10 grid place-content-center disabled:opacity-50 transition-opacity'
                            >
                                <Icon
                                    height={24}
                                    width={15}
                                    id='ChevronLeft'
                                    class='text-neutral-2'
                                />
                            </Slider.PrevButton>
                            <Slider.NextButton
                                id={id}
                                class='absolute right-0 lg:-right-12 top-[45%] h-10 w-10 grid place-content-center disabled:opacity-50 transition-opacity'
                            >
                                <Icon
                                    height={24}
                                    width={15}
                                    id='ChevronRight'
                                    class='text-neutral-2'
                                />
                            </Slider.NextButton>

                            {/* Zoom message */}
                            <div class='hidden lg:flex gap-x-2 items-center justify-center mt-2'>
                                <Icon id='ProductZoom' width={21} height={20} />
                                <span class='text-sm text-neutral-2'>{texts.imageHoverText}</span>
                            </div>
                        </div>
                    </div>

                    {/* Product Info */}
                    <div class='sm:col-start-3 sm:col-span-1 row-start-1 flex flex-col max-w-[392px] lg:w-[392px]'>
                        <ProductInfo
                            href={href}
                            page={page}
                            similars={similars}
                            selectedSkuId={selectedSkuId}
                            seals={seals}
                            timers={timers}
                            texts={texts}
                            paymentMethods={paymentMethods}
                            paymentMethodsButtonText={paymentMethodsButtonText}
                            pixDiscount={pixDiscount}
                            hidePixDiscountInProduct={hidePixDiscountInProduct}
                            similarsVariantions={similarsVariantions}
                            renderOnlyShipping={renderOnlyShipping}
                            shippingVariant={shippingVariant}
                            appPrice={appPrice}
                            descriptionVariant={descriptionVariant}
                            campaignStudio={campaignStudio}
                        />
                    </div>
                </div>
            </div>

            <SliderJS rootId={id} buttonsId={id} infinite />
        </>
    )
}

function ProductDetails(
    {
        page,
        similars,
        selectedSkuId,
        seals = [],
        timers = [],
        href,
        texts,
        paymentMethods,
        paymentMethodsButtonText,
        pixDiscount,
        hidePixDiscountInProduct,
        imagesOneToFour,
        imagesFiveToEightRender,
        similarsVariantions,
        renderOnlyShipping,
        shippingVariant,
        appPrice,
        descriptionVariant,
        campaignStudio,
    }: SectionProps<typeof loader>,
) {
    const { product } = page ?? {}
    const safeProduct = product
        ? product
        : { additionalProperty: [], inProductGroupWithID: 0 } as unknown as Product
    // Hook must be called unconditionally with a safe fallback product
    const { filteredTimers } = useTimers({ timers, product: safeProduct })
    if (!product) return null
    const shelfTimers = filteredTimers.filter(({ showInPDP }) => showInPDP)
    return (
        <div class=''>
            {page
                ? (
                    <Details
                        page={page}
                        similars={similars}
                        selectedSkuId={selectedSkuId}
                        seals={seals}
                        href={href}
                        timers={shelfTimers}
                        texts={texts}
                        paymentMethods={paymentMethods}
                        paymentMethodsButtonText={paymentMethodsButtonText}
                        pixDiscount={pixDiscount}
                        hidePixDiscountInProduct={hidePixDiscountInProduct}
                        imagesFiveToEightRender={imagesFiveToEightRender}
                        imagesOneToFour={imagesOneToFour}
                        similarsVariantions={similarsVariantions}
                        renderOnlyShipping={renderOnlyShipping}
                        shippingVariant={shippingVariant}
                        appPrice={appPrice}
                        descriptionVariant={descriptionVariant}
                        campaignStudio={campaignStudio}
                    />
                )
                : <NotFound />}
        </div>
    )
}
export async function loader(
    props: Props,
    _req: Request,
    ctx: AppContext & { config: GlobalConfig },
) {
    const url = new URL(_req.url)
    let selectedSkuId = url.searchParams.get('skuId')
    const href = url.href
    const { page } = props
    const { product } = page ?? {}
    const { sku, isVariantOf } = product ?? {}
    const pixDiscount = ctx?.config?.pixDiscount
    const hidePixDiscountInProduct = ctx?.config?.hidePixDiscountInProduct

    // Se houver apenas uma variação de SKU E essa variação for a UN, já seta o selectedSkuId com o valor do Sku - ticket 0924-000055
    const hasVariant = isVariantOf?.hasVariant
    const skuIdIsUnique = (hasVariant?.length === 1)
        ? hasVariant?.[0]?.name?.toLocaleLowerCase() === 'un'
        : false
    !!skuIdIsUnique && !!sku && !selectedSkuId ? selectedSkuId = sku : selectedSkuId
    if (!props.page) {
        ctx.response.status = 404
    }
    const similars = await fetchSimilars(product)
    return {
        ...props,
        href,
        selectedSkuId,
        similars,
        pixDiscount,
        hidePixDiscountInProduct,
    }
}
export default ProductDetails
